import { useMemo, useState } from "react";
import { BookOpenCheck, ExternalLink, Search, Server } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  filterMcpConsoleTools,
  getMcpConsoleStats,
  MCP_CONSOLE_CATEGORIES,
  MCP_CONSOLE_TOOLS,
  type McpConsoleCategory,
  type McpConsoleToolKind,
} from "@shared/mcp-console-catalog";

const badgeVariant: Record<McpConsoleToolKind, "secondary" | "outline" | "default"> = {
  read: "secondary",
  write: "outline",
  approval: "default",
};

export default function InternalMcpConsolePage() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<"all" | McpConsoleCategory>("all");
  const stats = getMcpConsoleStats();
  const visibleTools = useMemo(
    () => filterMcpConsoleTools(MCP_CONSOLE_TOOLS, search, category),
    [search, category],
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 pb-24 sm:p-6">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <BookOpenCheck className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-mcp-console-title">MCP Console</h1>
          <Badge variant="secondary">Internal · Master only</Badge>
        </div>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Read-only reference for the tools registered by the PersonaOS MCP server. This page cannot invoke or configure tools.
        </p>
      </header>

      <Card data-testid="card-mcp-connection-guide">
        <CardContent className="space-y-5 p-5">
          <div className="flex items-start gap-3">
            <Server className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0 space-y-1">
              <h2 className="font-semibold">Connect an AI assistant</h2>
              <p className="text-sm text-muted-foreground">
                Add the PersonaOS remote MCP server to Claude or ChatGPT, then sign in when prompted.
              </p>
            </div>
          </div>

          <div className="rounded-md border bg-muted/40 p-3">
            <div className="mb-1 text-xs font-medium text-muted-foreground">MCP server URL</div>
            <code className="select-all break-all text-sm font-semibold" data-testid="text-mcp-server-url">
              https://personaos.net/mcp
            </code>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Claude</h3>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                <li>Open Settings, then Connectors.</li>
                <li>Select Add custom connector.</li>
                <li>Enter the MCP server URL above and select Add.</li>
                <li>Enable PersonaOS in a conversation and complete sign-in.</li>
              </ol>
              <a
                href="https://support.anthropic.com/en/articles/11175166-about-integrations-using-remote-mcp"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Claude setup guide <ExternalLink className="h-3 w-3" />
              </a>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">ChatGPT</h3>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                <li>Enable Developer mode in Settings, Apps, Advanced settings.</li>
                <li>Open Settings, Apps, then Create.</li>
                <li>Enter the MCP server URL above and create the app.</li>
                <li>Select the draft PersonaOS app from the tools menu and complete sign-in.</li>
              </ol>
              <p className="text-xs text-muted-foreground">
                Custom MCP apps require a supported ChatGPT Business or Enterprise/Edu workspace.
              </p>
              <a
                href="https://help.openai.com/en/articles/12584461-developer-mode-and-mcp-apps-in-chatgpt"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                ChatGPT setup guide <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Catalog summary">
        {[
          ["Total tools", stats.total],
          ["Categories", stats.categories],
          ["Write-capable", stats.writeCapable],
          ["Approval-gated", stats.approvalGated],
        ].map(([label, value]) => (
          <Card key={label}>
            <CardContent className="p-4">
              <div className="text-2xl font-semibold">{value}</div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search tools, descriptions, or categories"
            className="pl-9"
            aria-label="Search MCP tools"
            data-testid="input-mcp-tool-search"
          />
        </div>
        <nav className="flex gap-2 overflow-x-auto pb-1" aria-label="Tool categories">
          {[{ id: "all", label: "All" } as const, ...MCP_CONSOLE_CATEGORIES].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setCategory(item.id)}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                category === item.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"
              }`}
              aria-pressed={category === item.id}
              data-testid={`button-mcp-category-${item.id}`}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </section>

      {visibleTools.length === 0 ? (
        <Card data-testid="empty-mcp-tools">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No tools match this search and category.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-7">
          {MCP_CONSOLE_CATEGORIES.map((group) => {
            const tools = visibleTools.filter((tool) => tool.category === group.id);
            if (!tools.length) return null;
            return (
              <section key={group.id} className="space-y-3" data-testid={`section-mcp-category-${group.id}`}>
                <h2 className="text-lg font-semibold">{group.label} <span className="text-sm font-normal text-muted-foreground">({tools.length})</span></h2>
                <div className="space-y-2">
                  {tools.map((tool) => (
                    <Card key={tool.name} data-testid={`card-mcp-tool-${tool.name}`}>
                      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 space-y-1.5">
                          <code className="break-all text-sm font-semibold text-foreground">{tool.name}</code>
                          <p className="text-sm leading-relaxed text-muted-foreground">{tool.description}</p>
                        </div>
                        <Badge variant={badgeVariant[tool.kind]} className="w-fit shrink-0 capitalize">{tool.kind}</Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}